import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import DateFnsUtils from '@date-io/date-fns'
import {
  MuiPickersUtilsProvider,
  DatePicker,
  TimePicker,
} from '@material-ui/pickers'
import * as R from 'ramda'
import momentTimezone from 'moment-timezone'
import moment from 'moment'
import clsx from 'clsx'
import { useField } from 'formik'
import { makeStyles } from '@material-ui/core'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { DISPLAY_FORMAT_DATE_US, API_DATE_FORMATTED } from 'libs/dateUtils'
import { getDatePickerDisplayFormat } from 'commons/date'
import LocalizedDatePickerProvider from 'app/components/datepickers/LocalizedDatePickerProvider'
import LocalizedDatePickerToolbar from 'app/components/datepickers/LocalizedDatePickerToolbar'

const useStyles = makeStyles(() => ({
  inputBorderError: {
    '& .MuiOutlinedInput-input': {
      border: '1px solid red',
      borderRadius: '4px',
    },
  },
  disabled: {
    opacity: 0.5,
  },
}))

const DatetimeFormik = ({
  className,
  name,
  required = false,
  minDate = null,
  maxDate = null,
  onChange,
  isShowDateOnly = false,
  isShowTimeOnly = false,
  timezone = momentTimezone.tz.guess(),
  dateLabel,
  timeLabel,
  disabled = false,
  isShowLabel = false,
}) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const [field, meta, helpers] = useField({ name })
  const { setValue } = helpers
  const fieldValue = field.value

  const isError = !R.isNil(meta.error) && meta.touched && !disabled

  const handleSetDateTime = useCallback(
    (newDateTime) => {
      const formattedDateTime = newDateTime.utc().format()
      setValue(formattedDateTime)
      if (onChange) onChange(formattedDateTime)
    },
    [onChange, setValue],
  )

  const handleDateChange = (value) => {
    const selectedDate = momentTimezone
      .tz(moment(value).format(API_DATE_FORMATTED), timezone)
      .set({ second: 0 })

    const currentTime =
      R.isNil(fieldValue) || fieldValue === ''
        ? moment().set({ second: 0 })
        : moment(fieldValue).set({ second: 0 })

    let newDateTime = selectedDate.set({
      hour: currentTime.get('hour'),
      minute: currentTime.get('minute'),
      second: 0,
    })

    handleSetDateTime(newDateTime)
  }

  const handleTimeChange = (value) => {
    const selectedTime = momentTimezone
      .tz(moment(value).format(API_DATE_FORMATTED), timezone)
      .set({ second: 0 })

    const currentDate =
      R.isNil(fieldValue) || fieldValue === ''
        ? moment().set({ second: 0 })
        : moment(fieldValue).set({ second: 0 })

    let newDateTime = currentDate.set({
      hour: selectedTime.get('hour'),
      minute: selectedTime.get('minute'),
      second: 0,
    })

    handleSetDateTime(newDateTime)
  }

  const getFormattedValue = (value) => {
    return value
      ? momentTimezone(value).tz(timezone).format(API_DATE_FORMATTED)
      : null
  }

  return (
    <div className={className}>
      <div className="sm:flex sm:space-x-5 space-y-5 sm:space-y-0">
        {!isShowTimeOnly && (
          <LocalizedDatePickerProvider>
            <div className={clsx('w-full', disabled && classes.disabled)}>
              {isShowLabel && (
                <div className="w-fit flex text-14 font-medium text-blue-950 mb-4">
                  {`${
                    dateLabel ||
                    formatMessage({
                      id: 'planAndBilling.usageHistory.table.date.label',
                    })
                  } ${required ? '*' : ''}`}
                </div>
              )}
              <DatePicker
                className={clsx(
                  'w-full mb-1 sm:mb-0 bg-white rounded-8 border border-grey-input text-blue-950',
                  disabled && 'cursor-not-allowed',
                  isError && classes.inputBorderError,
                )}
                placeholder={dateLabel}
                disabled={disabled}
                inputVariant="outlined"
                value={getFormattedValue(fieldValue)}
                onChange={handleDateChange}
                minDate={minDate && !disabled ? moment(minDate).format() : null}
                maxDate={maxDate && !disabled ? moment(maxDate).format() : null}
                format={getDatePickerDisplayFormat()}
                error={false}
                helperText=""
                ToolbarComponent={LocalizedDatePickerToolbar}
                okLabel={formatMessage({ id: 'date.picker.ok' })}
                cancelLabel={formatMessage({ id: 'date.picker.cancel' })}
                clearLabel={formatMessage({ id: 'date.picker.clear' })}
              />
            </div>
          </LocalizedDatePickerProvider>
        )}

        {!isShowDateOnly && (
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <div className={clsx('w-full', disabled && classes.disabled)}>
              {isShowLabel && (
                <div className="w-fit flex text-14 font-medium text-blue-950 mb-4">
                  {`${
                    timeLabel ||
                    formatMessage({
                      id: 'notificationBeacon.table.time.label',
                    })
                  } ${required ? '*' : ''}`}
                </div>
              )}
              <TimePicker
                className={clsx(
                  'flex-1 w-full mb-1 sm:mb-0 bg-white rounded-8 border font-medium',
                  disabled && 'cursor-not-allowed',
                  isError
                    ? classes.inputBorderError
                    : 'border border-grey-input text-blue-950',
                )}
                placeholder={timeLabel}
                inputVariant="outlined"
                value={getFormattedValue(fieldValue)}
                onChange={handleTimeChange}
                disabled={disabled}
              />
            </div>
          </MuiPickersUtilsProvider>
        )}
      </div>

      {isError && (
        <div className="flex space-x-2 py-2">
          <div className="flex my-auto">
            <ErrorOutlineIcon className="text-red-500" fontSize="small" />
          </div>
          <label className="text-red text-14">{meta.error}</label>
        </div>
      )}
    </div>
  )
}

export default React.memo(DatetimeFormik)
